"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo } from "react";

import { Loader } from "@/dashboard/components/ui/loader";
import { DASHBOARD_ROUTES } from "@/dashboard/constants/routes";
import { useAuth } from "@/dashboard/contexts/auth-context";

function safeRedirect(raw: string | null | undefined): string {
  if (!raw || /^https?:\/\//i.test(raw) || raw.startsWith("//")) {
    // nuke absolute URLs
    return DASHBOARD_ROUTES.HUB_FLAGGED;
  }
  return raw.startsWith("/") ? raw : `/${raw}`;
}

export function withGuestPage<P extends object>(
  Component: React.ComponentType<P>,
  options?: { redirectTo?: string }
): React.FC<P> {
  return function GuestProtectedPage(props: P) {
    const { user, authResolved } = useAuth();
    const router = useRouter();
    const searchParams = useSearchParams();

    /* -----------------------------------------------
     * Compute target *once* so it never flip-flops
     * --------------------------------------------- */
    const redirectTarget = useMemo(() => {
      if (options?.redirectTo) {
        return options.redirectTo;
      }

      const qp = searchParams?.get("redirectTo");
      return safeRedirect(qp);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (authResolved && user) {
        router.replace(redirectTarget);
      }
    }, [authResolved, user, router, redirectTarget]);

    /* --------------- loading / blocking --------------- */
    if (!authResolved) {
      return <Loader variant="fullscreen" />;
    }

    if (user) {
      return null;
    }

    return <Component {...props} />;
  };
}
